import React from 'react';

import debounce from 'lodash.debounce';

import { useMetaDataState } from '@/core/features/a-dynamic-page/hooks/meta-data-state';
import { useNavigationContextValue } from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-query';

export default function useTravelFormCollapseLoading({
    isPageLoading,
    loadingStateHandling,
}: {
    isPageLoading?: boolean;
    loadingStateHandling: 'auto' | 'manual';
}) {
    const metaState = useMetaDataState();
    const navigationContext = useNavigationContextValue();
    const isS1 = metaState?.layoutId === 'Mobile_S1_V1';
    const [showPageLoading, setShowPageLoading] = React.useState(false);

    const updateLoadingState = React.useCallback(() => {
        if (navigationContext.disableLoadingAnimation) {
            setShowPageLoading(false);
            return;
        }
        if (isPageLoading !== undefined && loadingStateHandling === 'manual') {
            setShowPageLoading(isPageLoading);
            return;
        }

        if (!isPageLoading) {
            setShowPageLoading(false);
            return;
        }
        if (!isS1 || window.scrollY > 90) {
            setShowPageLoading(true);
        }
    }, [navigationContext.disableLoadingAnimation, isPageLoading, isS1, loadingStateHandling]);

    React.useEffect(() => {
        updateLoadingState();
    }, [updateLoadingState]);

    const debouncedScrollHandler = React.useCallback(() => {
        return debounce(updateLoadingState, 60);
    }, [updateLoadingState]);

    React.useEffect(() => {
        window.addEventListener('scroll', debouncedScrollHandler());

        return () => {
            window.removeEventListener('scroll', debouncedScrollHandler());
        };
    }, [debouncedScrollHandler]);

    return showPageLoading;
}
